import { GameState, PlayerColor, Theme } from './types';
import {
  TRACK, HOME_COLS, HOME_SLOTS,
  SAFE_INDICES, TRACK_OFFSET,
  stepsToCoord,
} from './board';
import { t, playerName } from './i18n';
import { animPieceSteps } from './game';

// ── Статические таблицы (не зависят от стейта/лейаута) ────────────────────
// Вынесены на уровень модуля, чтобы не аллоцироваться на каждый rAF-кадр.
const DICE_DOTS: [number, number][][] = [
  [],
  [[0, 0]],
  [[-1, -1], [1, 1]],
  [[-1, -1], [0, 0], [1, 1]],
  [[-1, -1], [1, -1], [-1, 1], [1, 1]],
  [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]],
  [[-1, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [1, 1]],
];

const BASES: [PlayerColor, number, number][] = [
  ['red',    9,  0],
  ['green',  0,  0],
  ['yellow', 0,  9],
  ['blue',   9,  9],
];

const GATE_ARROWS: [number, number, 'up'|'down'|'left'|'right'][] = [
  [14, 7, 'up'],     // Red — вход в col-7 снизу
  [7,  0, 'right'],  // Green — вход в row-7 слева
  [0,  7, 'down'],   // Yellow — вход в col-7 сверху
  [7, 14, 'left'],   // Blue — вход в row-7 справа
];

const PLAYERS_ORDER: PlayerColor[] = ['red', 'green', 'yellow', 'blue'];

// Переиспользуется между кадрами для группировки фишек по клеткам — во
// избежание аллокации Map на каждый вызов renderFrame. Живёт только внутри
// renderFrame (очищается в начале группировки), наружу ссылка не уходит.
const posMap = new Map<string, { player: PlayerColor; pieceId: number; steps: number }[]>();

export interface Layout {
  boardX: number;   // пиксели левого края доски
  boardY: number;   // пиксели верхнего края доски
  boardSize: number;
  cell: number;     // размер одной клетки
}

export function computeLayout(W: number, H: number): Layout {
  const boardSize = Math.min(W, H * 0.88);
  const boardX = (W - boardSize) / 2;
  const boardY = (H - boardSize) / 2 - H * 0.04;
  const cell = boardSize / 15;
  return { boardX, boardY, boardSize, cell };
}

// ── Пиксельный центр клетки (row, col) ───────────────────────────────────
function cellCenter(layout: Layout, row: number, col: number): [number, number] {
  return [
    layout.boardX + col * layout.cell + layout.cell / 2,
    layout.boardY + row * layout.cell + layout.cell / 2,
  ];
}

// ── Округлённый прямоугольник ─────────────────────────────────────────────
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

// ── Рисование фишки ───────────────────────────────────────────────────────
function drawPiece(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  color: string, stroke: string,
  highlight: boolean
): void {
  // Тень
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur  = r * 0.6;

  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  ctx.shadowBlur = 0;

  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = stroke;
  ctx.lineWidth = r * 0.18;
  ctx.stroke();

  // Блик
  ctx.beginPath();
  ctx.arc(cx - r * 0.25, cy - r * 0.28, r * 0.32, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.fill();

  if (highlight) {
    ctx.beginPath();
    ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,0,0.85)';
    ctx.lineWidth = 2.5;
    ctx.stroke();
  }
}

// ── Рисование кубика ──────────────────────────────────────────────────────
function drawDice(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number,
  value: number, active: boolean,
  theme: Theme
): void {
  const half = size / 2;
  const r    = size * 0.15;

  roundRect(ctx, cx - half, cy - half, size, size, r);
  ctx.fillStyle = active ? theme.diceActive : theme.diceBg;
  ctx.fill();
  ctx.strokeStyle = theme.diceBorder;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  if (value === 0) return;

  const dotR = size * 0.09;
  ctx.fillStyle = active ? theme.btnText : theme.diceDot;

  for (const [dx, dy] of DICE_DOTS[value]) {
    const px = cx + dx * (half * 0.52);
    const py = cy + dy * (half * 0.52);
    ctx.beginPath();
    ctx.arc(px, py, dotR, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ── Звёздочка ─────────────────────────────────────────────────────────────
function drawStar(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number
): void {
  const r1 = size * 0.38;
  const r2 = size * 0.16;
  const pts = 5;
  ctx.beginPath();
  for (let i = 0; i < pts * 2; i++) {
    const angle = (i * Math.PI) / pts - Math.PI / 2;
    const r = i % 2 === 0 ? r1 : r2;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = 'rgba(243,156,18,0.7)';
  ctx.fill();
}

// ── Позиция кнопки debug (слева от кубика, зеркально рестарту) ───────────
export function debugBtnBounds(
  layout: Layout, _W: number, H: number
): { cx: number; cy: number; r: number } {
  const uiY = layout.boardY + layout.boardSize + layout.cell * 0.3;
  const uiH = H - uiY - layout.cell * 0.2;
  const cy  = uiY + uiH / 2;
  const r   = Math.min(layout.cell * 0.85, uiH * 0.38);
  const cx  = r + layout.cell * 0.5;
  return { cx, cy, r };
}

// ── Позиция кнопки рестарта (используется и в renderer, и в input) ────────
export function restartBtnBounds(
  layout: Layout, W: number, H: number
): { cx: number; cy: number; r: number } {
  const uiY    = layout.boardY + layout.boardSize + layout.cell * 0.3;
  const uiH    = H - uiY - layout.cell * 0.2;
  const cy     = uiY + uiH / 2;
  const r      = Math.min(layout.cell * 0.85, uiH * 0.38);
  const cx     = W - r - layout.cell * 0.5;
  return { cx, cy, r };
}

export interface DebugDrag {
  player: PlayerColor;
  pieceId: number;
  x: number;   // пиксельная позиция курсора
  y: number;
}

// ── Меню (☰ → оверлей: Продолжить / Заново / В хаб) ───────────────────────
export interface MenuRect { x: number; y: number; w: number; h: number; }
export interface MenuCircle { cx: number; cy: number; r: number; }

const MENU_TOGGLE = 40;  // диаметр ☰-кнопки
const MENU_EDGE   = 14;  // отступ от края

// Единственный источник геометрии меню — используется и для рендера, и для хит-теста
export function menuRects(
  viewW: number, viewH: number
): { toggle: MenuCircle; panel: MenuRect; resume: MenuRect; restart: MenuRect; hub: MenuRect } {
  const toggle: MenuCircle = { cx: MENU_EDGE + MENU_TOGGLE / 2, cy: MENU_EDGE + MENU_TOGGLE / 2, r: MENU_TOGGLE / 2 };
  const bw = Math.min(260, viewW - 64);
  const bh = 52, gap = 14;
  const px = (viewW - bw) / 2;
  const total = bh * 3 + gap * 2;
  const py = (viewH - total) / 2;
  const resume:  MenuRect = { x: px, y: py,                  w: bw, h: bh };
  const restart: MenuRect = { x: px, y: py + (bh + gap),     w: bw, h: bh };
  const hub:     MenuRect = { x: px, y: py + (bh + gap) * 2, w: bw, h: bh };
  const panel:   MenuRect = { x: px - 20, y: py - 24, w: bw + 40, h: total + 48 };
  return { toggle, panel, resume, restart, hub };
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}

function drawMenu(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  theme: Theme,
  menuVisible: boolean,
  menuOpen: boolean
): void {
  if (!menuVisible) return;
  const { toggle, panel, resume, restart, hub } = menuRects(W, H);

  // ☰-кнопка
  ctx.save();
  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  ctx.beginPath();
  ctx.arc(toggle.cx, toggle.cy, toggle.r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = theme.textUiDim;
  ctx.font = 'bold 18px -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('☰', toggle.cx, toggle.cy + 1);
  ctx.textBaseline = 'alphabetic';
  ctx.restore();

  if (!menuOpen) return;

  // затемнение + панель
  ctx.save();
  ctx.fillStyle = theme.overlayBg;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = theme.boardBg;
  roundRectPath(ctx, panel.x, panel.y, panel.w, panel.h, 18);
  ctx.fill();
  ctx.strokeStyle = theme.boardBorder;
  ctx.lineWidth = 1;
  ctx.stroke();

  const items: [MenuRect, string, boolean][] = [
    [resume,  t('resume'),       false],
    [restart, t('restart_game'), false],
    [hub,     t('to_hub'),       true],   // акцентный
  ];
  for (const [r, label, accent] of items) {
    ctx.fillStyle = accent ? theme.colors.red : theme.trackCell;
    roundRectPath(ctx, r.x, r.y, r.w, r.h, 12);
    ctx.fill();
    ctx.fillStyle = theme.btnText;  // тёмный текст читаем и на белой пилюле, и на красной акцентной
    ctx.font = 'bold 17px -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
  }
  ctx.textBaseline = 'alphabetic';
  ctx.restore();
}

// ── Главная функция рендера ───────────────────────────────────────────────
export function renderFrame(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  layout: Layout,
  state: GameState,
  theme: Theme,
  _nowMs: number,
  debugMode = false,
  drag: DebugDrag | null = null,
  pendingStart = false,
  menuVisible = false,
  menuOpen = false
): void {
  const { boardX, boardY, boardSize, cell } = layout;

  // Сбрасываем состояние контекста — на случай если предыдущий кадр упал в середине
  ctx.globalAlpha = 1;
  ctx.shadowBlur  = 0;

  // ── Фон ──────────────────────────────────────────────────────────────
  ctx.fillStyle = theme.bg;
  ctx.fillRect(0, 0, W, H);

  // ── Доска: фон ───────────────────────────────────────────────────────
  roundRect(ctx, boardX, boardY, boardSize, boardSize, cell * 0.3);
  ctx.fillStyle = theme.boardBg;
  ctx.fill();
  ctx.strokeStyle = theme.boardBorder;
  ctx.lineWidth = 2;
  ctx.stroke();

  // ── Домашние базы (цветные углы) ──────────────────────────────────────
  for (const [color, baseRow, baseCol] of BASES) {
    const x = boardX + baseCol * cell;
    const y = boardY + baseRow * cell;
    const s = cell * 6;
    roundRect(ctx, x + 2, y + 2, s - 4, s - 4, cell * 0.5);
    ctx.fillStyle = theme.colorsLight[color];
    ctx.fill();

    // Внутренний круг базы
    const cx2 = x + s / 2;
    const cy2 = y + s / 2;
    ctx.beginPath();
    ctx.arc(cx2, cy2, s * 0.3, 0, Math.PI * 2);
    ctx.fillStyle = theme.colors[color];
    ctx.globalAlpha = 0.15;
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // ── Клетки главного трека ─────────────────────────────────────────────
  for (let i = 0; i < TRACK.length; i++) {
    const [row, col] = TRACK[i];
    const x = boardX + col * cell;
    const y = boardY + row * cell;
    const pad = cell * 0.05;

    ctx.fillStyle = theme.trackCell;
    roundRect(ctx, x + pad, y + pad, cell - pad * 2, cell - pad * 2, cell * 0.12);
    ctx.fill();
    ctx.strokeStyle = theme.boardBorder;
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Стартовые клетки цветов
    const colors: PlayerColor[] = ['red', 'green', 'yellow', 'blue'];
    for (const color of colors) {
      if (i === TRACK_OFFSET[color]) {
        ctx.fillStyle = theme.colors[color];
        ctx.globalAlpha = 0.25;
        roundRect(ctx, x + pad, y + pad, cell - pad * 2, cell - pad * 2, cell * 0.12);
        ctx.fill();
        ctx.globalAlpha = 1;
        break;
      }
    }

    // Безопасные клетки (звёздочка)
    if (SAFE_INDICES.has(i)) {
      const [ccx, ccy] = cellCenter(layout, row, col);
      drawStar(ctx, ccx, ccy, cell);
    }
  }

  // ── Стрелки на воротах домашних коридоров ────────────────────────────
  for (const [row, col, dir] of GATE_ARROWS) {
    const [acx, acy] = cellCenter(layout, row, col);
    drawGateArrow(ctx, acx, acy, cell, dir);
  }

  // ── Центр (финиш) — 4 стрелки сходятся к центру ─────────────────────
  // Каждый треугольник: основание на своей стороне 3×3 квадрата, вершина в центре.
  // Цвет определяется тем, с какой стороны заходит домашняя колонка игрока:
  //   Red   входит снизу  (col 7 ↑) → треугольник с основанием снизу
  //   Yellow входит сверху (col 7 ↓) → треугольник с основанием сверху
  //   Green  входит слева  (row 7 →) → треугольник с основанием слева
  //   Blue   входит справа (row 7 ←) → треугольник с основанием справа
  const cx0 = boardX + 6 * cell;
  const cy0 = boardY + 6 * cell;
  const cs  = cell * 3;
  const mid = cs / 2;
  const triangles: [PlayerColor, [number, number][]][] = [
    ['yellow', [[0,0],  [cs,0],  [mid,mid]]],   // основание сверху
    ['red',    [[0,cs], [cs,cs], [mid,mid]]],    // основание снизу
    ['green',  [[0,0],  [0,cs],  [mid,mid]]],    // основание слева
    ['blue',   [[cs,0], [cs,cs], [mid,mid]]],    // основание справа
  ];
  for (const [color, pts] of triangles) {
    ctx.beginPath();
    ctx.moveTo(cx0 + pts[0][0], cy0 + pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(cx0 + pts[i][0], cy0 + pts[i][1]);
    ctx.closePath();
    ctx.fillStyle = theme.colors[color];
    ctx.globalAlpha = 0.85;
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  // Центральный кружок (финишная точка)
  ctx.beginPath();
  ctx.arc(cx0 + mid, cy0 + mid, cell * 0.55, 0, Math.PI * 2);
  ctx.fillStyle = theme.boardBg;
  ctx.fill();
  ctx.strokeStyle = theme.boardBorder;
  ctx.lineWidth = 1;
  ctx.stroke();

  // ── Домашние колонки (поверх центральных треугольников) ───────────────
  // Рисуем после центра, чтобы 6-я клетка ([8,7] и т.д.) перекрывала фон центра
  const homeColors: PlayerColor[] = ['red', 'green', 'yellow', 'blue'];
  for (const color of homeColors) {
    for (const [row, col] of HOME_COLS[color]) {
      const x = boardX + col * cell;
      const y = boardY + row * cell;
      const pad = cell * 0.05;
      ctx.fillStyle = theme.colors[color];
      ctx.globalAlpha = 0.35;
      roundRect(ctx, x + pad, y + pad, cell - pad * 2, cell - pad * 2, cell * 0.12);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  // ── Фишки ─────────────────────────────────────────────────────────────
  const pieceR = cell * 0.32;
  const animPiece = state.anim;

  // Группируем фишки по клеткам, чтобы расставить рядом
  // (posMap — модульная переменная, переиспользуется между кадрами)
  posMap.clear();

  for (const piece of state.pieces) {
    // Пропускаем перетаскиваемую фишку (она рисуется отдельно поверх)
    if (drag && drag.player === piece.player && drag.pieceId === piece.id) continue;

    // Текущие шаги (учитываем анимацию)
    let steps = piece.steps;
    if (
      animPiece &&
      animPiece.player === piece.player &&
      animPiece.pieceId === piece.id
    ) {
      steps = animPieceSteps(state);
      if (steps === -2) continue;
    }
    if (steps === -1) {
      // В доме — рисуем в домашнем слоте
      const [row, col] = HOME_SLOTS[piece.player][piece.id];
      const [pcx, pcy] = cellCenter(layout, row, col);
      const highlight = state.currentPlayer === 'red' && piece.player === 'red' && state.selectablePieces.includes(piece.id);
      drawPiece(ctx, pcx, pcy, pieceR, theme.colors[piece.player], theme.pieceStroke, highlight);
      continue;
    }

    const coord = stepsToCoord(piece.player, steps);
    if (!coord) continue;
    const [row, col] = coord;
    const key = `${row},${col}`;
    if (!posMap.has(key)) posMap.set(key, []);
    posMap.get(key)!.push({ player: piece.player, pieceId: piece.id, steps });
  }

  // Рисуем фишки на треке
  for (const [key, group] of posMap) {
    const [row, col] = key.split(',').map(Number);
    const [baseCx, baseCy] = cellCenter(layout, row, col);
    const offsets = pieceOffsets(group.length, pieceR);
    group.forEach((entry, i) => {
      const ocx = baseCx + offsets[i][0];
      const ocy = baseCy + offsets[i][1];
      const isSelectable =
        entry.player === 'red' &&
        state.currentPlayer === 'red' &&
        state.phase === 'selecting' &&
        state.selectablePieces.includes(entry.pieceId);
      drawPiece(ctx, ocx, ocy, pieceR * (group.length > 1 ? 0.82 : 1),
        theme.colors[entry.player], theme.pieceStroke, isSelectable);
    });
  }

  // ── UI: нижняя панель ─────────────────────────────────────────────────
  const uiY = boardY + boardSize + cell * 0.3;
  const uiH = H - uiY - cell * 0.2;

  // Кубик
  const diceSize  = Math.min(cell * 2.2, uiH * 0.7);
  const diceCx    = W / 2;
  const diceCy    = uiY + uiH / 2;
  const diceActive = state.currentPlayer === 'red' && state.phase === 'idle';
  const diceValue  = state.dice.rolling
    ? Math.floor(_nowMs / 110) % 6 + 1
    : state.dice.value;

  drawDice(ctx, diceCx, diceCy, diceSize, diceValue, diceActive, theme);

  // Подпись "Бросить" под кубиком (только для игрока)
  if (diceActive) {
    ctx.fillStyle = theme.textUiDim;
    ctx.font = `${cell * 0.55}px -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(t('tap_to_roll'), diceCx, diceCy + diceSize / 2 + cell * 0.55);
  }

  // Чей ход (индикаторы игроков)
  const indicatorY = uiY + uiH * 0.15;
  const spacing    = W / 5;
  PLAYERS_ORDER.forEach((p, i) => {
    const ix = spacing + i * spacing;
    const iy = indicatorY;
    const isCurrent = p === state.currentPlayer;
    ctx.beginPath();
    ctx.arc(ix, iy, cell * (isCurrent ? 0.38 : 0.26), 0, Math.PI * 2);
    ctx.fillStyle = theme.colors[p];
    ctx.globalAlpha = isCurrent ? 1 : 0.35;
    ctx.fill();
    ctx.globalAlpha = 1;
    if (isCurrent) {
      ctx.strokeStyle = theme.textUi;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    ctx.fillStyle = isCurrent ? theme.textUi : theme.textUiDim;
    ctx.font = `${cell * 0.38}px -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(playerName(p), ix, iy + cell * 0.62);
  });

  // ── Кнопка рестарта ───────────────────────────────────────────────────
  const { cx: rCx, cy: rCy, r: rR } = restartBtnBounds(layout, W, H);
  ctx.beginPath();
  ctx.arc(rCx, rCy, rR, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = theme.textUiDim;
  ctx.font = `${rR * 1.1}px -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('↺', rCx, rCy);
  ctx.textBaseline = 'alphabetic';

  // ── Кнопка debug ─────────────────────────────────────────────────────
  const { cx: dCx, cy: dCy, r: dR } = debugBtnBounds(layout, W, H);
  ctx.beginPath();
  ctx.arc(dCx, dCy, dR, 0, Math.PI * 2);
  ctx.fillStyle = debugMode ? 'rgba(255,160,0,0.85)' : 'rgba(255,255,255,0.1)';
  ctx.fill();
  ctx.strokeStyle = debugMode ? 'rgba(255,160,0,1)' : 'rgba(255,255,255,0.25)';
  ctx.lineWidth = debugMode ? 2 : 1;
  ctx.stroke();
  ctx.fillStyle = debugMode ? '#fff' : theme.textUiDim;
  ctx.font = `bold ${dR * 0.9}px -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('D', dCx, dCy);
  ctx.textBaseline = 'alphabetic';

  if (debugMode) {
    // Метка "DEBUG" над кнопкой
    ctx.fillStyle = 'rgba(255,160,0,0.9)';
    ctx.font = `${cell * 0.32}px -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText('DEBUG', dCx, dCy - dR - cell * 0.1);
  }

  // ── Оверлей победы ────────────────────────────────────────────────────
  if (state.phase === 'won' && state.winner) {
    ctx.fillStyle = theme.overlayBg;
    ctx.fillRect(0, 0, W, H);

    ctx.textAlign = 'center';
    ctx.fillStyle = theme.overlayText;
    ctx.font = `bold ${cell * 1.4}px -apple-system, sans-serif`;
    ctx.fillText('🎉', W / 2, H / 2 - cell * 1.5);

    ctx.font = `bold ${cell * 0.9}px -apple-system, sans-serif`;
    const winnerName = state.winner === 'red'
      ? t('you_win')
      : t('player_wins', { name: playerName(state.winner) });
    ctx.fillText(winnerName, W / 2, H / 2 - cell * 0.3);

    ctx.fillStyle = theme.overlaySub;
    ctx.font = `${cell * 0.55}px -apple-system, sans-serif`;
    ctx.fillText(t('tap_to_play'), W / 2, H / 2 + cell * 0.6);
  }

  // ── Оверлей «нажмите чтобы начать» ──────────────────────────────────────
  if (pendingStart) {
    ctx.fillStyle = 'rgba(0,0,0,0.52)';
    ctx.fillRect(0, 0, W, H);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${cell * 0.85}px -apple-system, sans-serif`;
    ctx.fillText(t('tap_to_start'), W / 2, H / 2);
    ctx.textBaseline = 'alphabetic';
  }

  // ── Меню (☰ + оверлей) ─────────────────────────────────────────────────
  drawMenu(ctx, W, H, theme, menuVisible, menuOpen);

  // ── Перетаскиваемая фишка (поверх всего) ─────────────────────────────
  if (drag) {
    drawPiece(ctx, drag.x, drag.y, pieceR * 1.15, theme.colors[drag.player], theme.pieceStroke, true);
  }
}

// ── Стрелка на клетке-воротах ─────────────────────────────────────────────
function drawGateArrow(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number,
  dir: 'up' | 'down' | 'left' | 'right'
): void {
  const w = size * 0.28;
  const h = size * 0.42;
  ctx.beginPath();
  switch (dir) {
    case 'up':
      ctx.moveTo(cx,       cy - h / 2);
      ctx.lineTo(cx - w/2, cy + h / 2);
      ctx.lineTo(cx + w/2, cy + h / 2);
      break;
    case 'down':
      ctx.moveTo(cx,       cy + h / 2);
      ctx.lineTo(cx - w/2, cy - h / 2);
      ctx.lineTo(cx + w/2, cy - h / 2);
      break;
    case 'left':
      ctx.moveTo(cx - h/2, cy);
      ctx.lineTo(cx + h/2, cy - w / 2);
      ctx.lineTo(cx + h/2, cy + w / 2);
      break;
    case 'right':
      ctx.moveTo(cx + h/2, cy);
      ctx.lineTo(cx - h/2, cy - w / 2);
      ctx.lineTo(cx - h/2, cy + w / 2);
      break;
  }
  ctx.closePath();
  ctx.fillStyle = 'rgba(90,90,90,0.5)';
  ctx.fill();
}

// ── Смещения фишек при нескольких на одной клетке ─────────────────────────
function pieceOffsets(count: number, r: number): [number, number][] {
  if (count === 1) return [[0, 0]];
  if (count === 2) return [[-r * 0.55, 0], [r * 0.55, 0]];
  if (count === 3) return [[-r * 0.6, -r * 0.4], [r * 0.6, -r * 0.4], [0, r * 0.65]];
  // 4+ pieces: 2×2 grid; extra (animated piece passing through) overlap in center
  const offsets: [number, number][] = [[-r * 0.55, -r * 0.55], [r * 0.55, -r * 0.55], [-r * 0.55, r * 0.55], [r * 0.55, r * 0.55]];
  while (offsets.length < count) offsets.push([0, 0]);
  return offsets;
}

// ── Считывание темы из CSS-переменных ─────────────────────────────────────
export function readTheme(): Theme {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();
  return {
    bg:          v('--bg'),
    boardBg:     v('--board-bg'),
    boardBorder: v('--board-border'),
    trackCell:   v('--track-cell'),
    safeStar:    v('--safe-star'),
    colors: {
      red:    v('--col-red'),
      green:  v('--col-green'),
      yellow: v('--col-yellow'),
      blue:   v('--col-blue'),
    },
    colorsLight: {
      red:    v('--col-red-light'),
      green:  v('--col-green-light'),
      yellow: v('--col-yellow-light'),
      blue:   v('--col-blue-light'),
    },
    pieceStroke: v('--piece-stroke'),
    diceBg:      v('--dice-bg'),
    diceDot:     v('--dice-dot'),
    diceBorder:  v('--dice-border'),
    diceActive:  v('--dice-active'),
    textUi:      v('--text-ui'),
    textUiDim:   v('--text-ui-dim'),
    overlayBg:   v('--overlay-bg'),
    overlayText: v('--overlay-text'),
    overlaySub:  v('--overlay-sub'),
    btnBg:       v('--btn-bg'),
    btnText:     v('--btn-text'),
  };
}
